import * as React from "react"
import { Button, cn, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useRelativeTime } from "../../../lib/format-relative-time.js"

export interface SetGenerationStatusProps {
  generatedAt: string
  canRegen?: boolean
  isRegenerating?: boolean
  onRegen?: () => void
}

function formatAbsoluteGeneratedAt(iso: string, locale: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
}

export function SetGenerationStatus({
  generatedAt,
  canRegen = false,
  isRegenerating = false,
  onRegen,
}: SetGenerationStatusProps) {
  const { t, i18n } = useTranslation()
  const relativeTime = useRelativeTime(generatedAt)
  const absoluteAt = React.useMemo(
    () => formatAbsoluteGeneratedAt(generatedAt, i18n.language),
    [generatedAt, i18n.language],
  )
  const showRegen = canRegen && onRegen != null

  return (
    <div className="inline-flex shrink-0 items-stretch overflow-hidden rounded-md border border-border/80 bg-background shadow-xs">
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex min-h-8 max-w-[10.5rem] items-center gap-1.5 px-2.5 py-1 sm:max-w-none"
            role="status"
            tabIndex={0}
          >
            <span
              className="flex size-5 shrink-0 items-center justify-center rounded-full bg-warning/10"
              aria-hidden
            >
              <Icon name="clock" size="xs" className="text-warning" />
            </span>
            <span className="min-w-0 truncate text-xs leading-none sm:whitespace-nowrap">
              <span className="hidden text-muted-foreground sm:inline">
                {t("opportunities.setGeneration.prefix")}{" "}
              </span>
              <time dateTime={generatedAt} className="font-medium tabular-nums text-foreground">
                {relativeTime}
              </time>
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[16rem]">
          <p className="text-xs leading-snug">
            {t("opportunities.setGeneration.tooltip", { time: relativeTime })}
          </p>
          {absoluteAt != null && (
            <p className="mt-1 text-[11px] text-muted-foreground">{absoluteAt}</p>
          )}
        </TooltipContent>
      </Tooltip>

      {showRegen && (
        <>
          <div className="w-px self-stretch bg-border/80" aria-hidden />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-8 shrink-0 rounded-none hover:bg-muted/80"
                onClick={onRegen}
                disabled={isRegenerating}
                aria-label={t("opportunities.regenSet")}
              >
                <Icon
                  name="refresh-cw"
                  size="xs"
                  className={cn("text-muted-foreground", isRegenerating && "animate-spin")}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("opportunities.regenSet")}</TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  )
}
